import React, { useState } from 'react';
import { Shield } from 'lucide-react';
import { submitContactRequest } from '../lib/supabase';

interface PersonalInfoFormProps {
  selectedInsurances: string[];
  onBack: () => void;
}

export function PersonalInfoForm({ selectedInsurances, onBack }: PersonalInfoFormProps) {
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await submitContactRequest({
        name: `${personalInfo.firstName} ${personalInfo.lastName}`,
        email: personalInfo.email,
        phone: personalInfo.phone || undefined,
        message: `Anmodning om forsikringstilbud`,
        insurance_types: selectedInsurances
      });

      setSubmitSuccess(true);
      setPersonalInfo({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        consent: false
      });
    } catch (error) {
      setSubmitError('Der opstod en fejl. Prøv venligst igen.');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center fade-in">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 slide-up">
            <Shield className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 slide-up" style={{ animationDelay: '0.1s' }}>
            Tak for din henvendelse!
          </h2>
          <p className="text-gray-600 mb-6 slide-up" style={{ animationDelay: '0.2s' }}>
            Vi har modtaget din anmodning og vil kontakte dig inden for 24 timer.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700 transition-colors slide-up"
            style={{ animationDelay: '0.3s' }}
          >
            Tilbage til forsiden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 fade-in">
      <button
        onClick={onBack}
        className="text-green-600 hover:text-green-700 font-medium flex items-center mb-8 slide-up"
      >
        ← Tilbage
      </button>

      <div className="bg-white rounded-xl shadow-lg p-8 slide-up" style={{ animationDelay: '0.1s' }}>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Dine oplysninger</h2>
        <p className="text-gray-600 mb-6">
          Udfyld dine kontaktoplysninger, så vender vi tilbage med det bedste tilbud til dig
        </p>

        {submitError && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fornavn
              </label>
              <input
                type="text"
                required
                className="w-full rounded-lg border-gray-300 focus:border-green-500 focus:ring-green-500"
                value={personalInfo.firstName}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Efternavn
              </label>
              <input
                type="text"
                required
                className="w-full rounded-lg border-gray-300 focus:border-green-500 focus:ring-green-500"
                value={personalInfo.lastName}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              required
              className="w-full rounded-lg border-gray-300 focus:border-green-500 focus:ring-green-500"
              value={personalInfo.email}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, email: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefonnummer
            </label>
            <input
              type="tel"
              required
              className="w-full rounded-lg border-gray-300 focus:border-green-500 focus:ring-green-500"
              value={personalInfo.phone}
              onChange={(e) => setPersonalInfo(prev => ({ ...prev, phone: e.target.value }))}
            />
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                required
                id="consent"
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                checked={personalInfo.consent}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, consent: e.target.checked }))}
              />
            </div>
            <label htmlFor="consent" className="ml-3 text-sm text-gray-600">
              Jeg accepterer, at AutoInsurance.dk må kontakte mig med et forsikringstilbud og behandle mine oplysninger i henhold til persondatapolitikken.
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !personalInfo.consent}
            className={`w-full bg-green-600 text-white py-3 px-6 rounded-lg font-medium transition-colors ${
              (isSubmitting || !personalInfo.consent)
                ? 'opacity-75 cursor-not-allowed'
                : 'hover:bg-green-700'
            }`}
          >
            {isSubmitting ? 'Sender...' : 'Send anmodning'}
          </button>
        </form>
      </div>
    </div>
  );
}